/**
 * Chat History - Read-only message log for completed orders.
 *
 * Fetches messages from our backend API (not CometChat SDK)
 * since completed orders may no longer have active CometChat groups.
 * Displayed as a collapsible GlassCard.
 */

import React, { useEffect, useState } from 'react';
import { Box, Typography, Collapse, IconButton } from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import GlassCard from './GlassCard';
import { useMissionTheme } from '../theme';
import config from '../../../config';

interface HistoryMessage {
  id: string;
  senderRole: string;
  messageType: string;
  content: string | null;
  photoUrl: string | null;
  createdAt: string;
  sender: { id: string; firstName: string; lastName: string } | null;
}

interface ChatHistoryProps {
  orderId: string;
}

export default function ChatHistory({ orderId }: ChatHistoryProps) {
  const { mc } = useMissionTheme();
  const [messages, setMessages] = useState<HistoryMessage[]>([]);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = JSON.parse(localStorage.getItem('auth') || '{}')?.accessToken;
    if (!token) return;

    fetch(`${config.API_URL}/admin/orders/${orderId}/messages?limit=100`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => {
        setMessages((data?.data || []).reverse());
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [orderId]);

  if (loading || messages.length === 0) return null;

  return (
    <GlassCard
      title={`Chat History (${messages.length})`}
      icon={<ChatIcon sx={{ fontSize: 18 }} />}
      iconColor={mc.textMuted}
      headerAction={
        <IconButton size="small" onClick={() => setExpanded(!expanded)} sx={{ color: mc.textMuted }}>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      }
      sx={{ gridColumn: '1 / -1' }}
    >
      <Collapse in={expanded}>
        <Box sx={{ maxHeight: 400, overflowY: 'auto', pr: 0.5 }}>
          {messages.map((msg) => {
            const name = msg.sender
              ? `${msg.sender.firstName} ${msg.sender.lastName}`.trim()
              : msg.senderRole === 'system' ? 'System' : 'Unknown';
            const isSystem = msg.senderRole === 'system';
            const time = new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

            return (
              <Box key={msg.id} sx={{ mb: 1.5, textAlign: isSystem ? 'center' : 'left' }}>
                {isSystem ? (
                  <Typography variant="caption" sx={{ color: mc.textMuted, fontStyle: 'italic' }}>
                    {msg.content} — {time}
                  </Typography>
                ) : (
                  <>
                    <Typography variant="caption" sx={{ color: mc.secondary, fontSize: '0.7rem' }}>
                      {name} ({msg.senderRole}) · {time}
                    </Typography>
                    {msg.photoUrl && (
                      <Box
                        component="img"
                        src={msg.photoUrl}
                        sx={{ display: 'block', width: 160, height: 160, objectFit: 'cover', borderRadius: 1, mt: 0.5 }}
                      />
                    )}
                    {msg.content && (
                      <Typography variant="body2" sx={{ color: mc.textPrimary, fontSize: '0.85rem' }}>
                        {msg.content}
                      </Typography>
                    )}
                  </>
                )}
              </Box>
            );
          })}
        </Box>
      </Collapse>
      {!expanded && (
        <Typography
          variant="caption"
          sx={{ color: mc.textMuted, cursor: 'pointer' }}
          onClick={() => setExpanded(true)}
        >
          Click to expand chat log
        </Typography>
      )}
    </GlassCard>
  );
}
